import React, { useEffect } from "react";
import { Modal, Form, Input, notification } from "antd";
import {
  editLender,
  UpdateLenderBody,
} from "../../../services/adminController";
import { useMutation, useQueryClient } from "@tanstack/react-query";

type BankDetails = {
  bankName: string;
  accountNumber: string;
  bankHolderName: string;
  ifsc: string;
  branchName: string;
  branchAddress: string;
};

type EditModalProps = {
  open: boolean;
  onCancel: () => void;
  initialData: {
    clientId: string;
    address: string;
    email: string;
    mobileNumber: string;
    bankDetails: BankDetails;
    clientName: string;
    companyName: string;
  } | null;
  queryClient: ReturnType<typeof useQueryClient>;
};

export const EditModal: React.FC<EditModalProps> = ({
  open,
  onCancel,
  initialData,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        address: initialData.address,
        email: initialData.email,
        mobileNumber: initialData.mobileNumber,
        ...initialData.bankDetails,
      });
    } else {
      form.resetFields();
    }
  }, [initialData, form]);

  const { mutate, isPending } = useMutation({
    mutationFn: ({
      clientId,
      body,
    }: {
      clientId: string;
      body: UpdateLenderBody;
    }) => editLender(clientId, body),
    onSuccess: (res) => {
      notification.success({
        message: "Success",
        description: res?.data?.message || "Lender updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["lenders"] });
      onCancel();
    },
    onError: (error: any) => {
      notification.error({
        message: "Error",
        description:
          error?.response?.data?.message || "Failed to update lender",
      });
    },
  });

  const handleFinish = (values: any) => {
    if (!initialData?.clientId) return;

    const body: UpdateLenderBody = {
      address: values.address,
      email: values.email,
      mobileNumber: values.mobileNumber,
      clientName: initialData.clientName,
      companyName: initialData.companyName,
      bankDetails: {
        bankName: values.bankName,
        accountNumber: values.accountNumber,
        bankHolderName: values.bankHolderName,
        ifsc: values.ifsc,
        branchName: values.branchName,
        branchAddress: values.branchAddress,
      },
    };

    mutate({ clientId: initialData.clientId, body });
  };

  return (
    <Modal
      open={open}
      title="Edit Lender Details"
      onCancel={onCancel}
      onOk={() => form.submit()}
      confirmLoading={isPending}
      okText="Save Changes"
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item label="Name">
          <Input value={initialData?.clientName || "-"} disabled />
        </Form.Item>

        <Form.Item label="Company">
          <Input value={initialData?.companyName || "-"} disabled />
        </Form.Item>

        <Form.Item label="Address" name="address">
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item label="Email" name="email" rules={[{ type: "email" }]}>
          <Input />
        </Form.Item>

        <Form.Item label="Mobile Number" name="mobileNumber">
          <Input maxLength={10} />
        </Form.Item>

        <Form.Item label="Bank Name" name="bankName">
          <Input />
        </Form.Item>

        <Form.Item label="Account Number" name="accountNumber">
          <Input />
        </Form.Item>

        <Form.Item label="Account Holder Name" name="bankHolderName">
          <Input />
        </Form.Item>

        <Form.Item label="IFSC Code" name="ifsc">
          <Input />
        </Form.Item>

        <Form.Item label="Branch Name" name="branchName">
          <Input />
        </Form.Item>

        <Form.Item label="Branch Address" name="branchAddress">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditModal;
